import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { UserPlus, AlertCircle } from 'lucide-react';
import { useMerchantAuth } from '../../contexts/MerchantAuthContext';
import { toast } from 'sonner';

export default function MerchantRegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    callbackUrl: '',
    returnUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const { register } = useMerchantAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);

    const result = await register(
      formData.name,
      formData.email,
      formData.password,
      formData.callbackUrl || null,
      formData.returnUrl || null
    );
    
    if (result.success) {
      // Show API key (only shown once!)
      if (result.data.merchant.api_key) {
        setApiKey(result.data.merchant.api_key);
        setShowApiKey(true);
        toast.success('¡Cuenta creada exitosamente!');
      } else {
        toast.success('¡Bienvenido!');
        navigate('/portal/dashboard');
      }
    } else {
      toast.error(result.error || 'Error al registrar');
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/portal/dashboard');
  };

  if (showApiKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--brand-sand))]">
        <Card className="w-full max-w-2xl p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <AlertCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
              ¡Cuenta Creada Exitosamente!
            </h2>
          </div>

          <div className="space-y-6">
            <div className="p-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
              <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                IMPORTANTE: Guarda tu API Key
              </h3>
              <p className="text-sm text-yellow-800 mb-4">
                Esta es tu API Key para integrar FluxiPay en tu aplicación. <strong>Sólo se mostrará una vez</strong>.
                Guárdala en un lugar seguro.
              </p>
              <div className="bg-white p-4 rounded border border-yellow-300">
                <code className="text-sm break-all text-[hsl(var(--brand-ink))] font-mono">
                  {apiKey}
                </code>
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(apiKey);
                  toast.success('API Key copiada al portapapeles');
                }}
                variant="outline"
                className="w-full mt-4"
              >
                Copiar API Key
              </Button>
            </div>

            <div className="text-sm text-[hsl(var(--muted-foreground))] space-y-2">
              <p>✅ Usa esta API Key para autenticar tus requests</p>
              <p>✅ Podrás regenerarla desde Configuración</p>
              <p>✅ Nunca compartas tu API Key con nadie</p>
            </div>

            <Button
              onClick={handleContinue}
              className="w-full"
              size="lg"
            >
              Continuar al Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--brand-sand))] py-12">
      <Card className="w-full max-w-2xl p-8" data-testid="merchant-register-card">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--brand-teal))]/10 mb-4">
            <UserPlus className="w-8 h-8 text-[hsl(var(--brand-teal))]" />
          </div>
          <h1 className="text-2xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
            Crear Cuenta de Merchant
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Comienza a aceptar pagos crypto en minutos
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="merchant-register-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre del Negocio *</Label>
              <Input
                id="name"
                name="name"
                placeholder="Mi Empresa"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="merchant-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="merchant-reg-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña *</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Mínimo 8 caracteres"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="merchant-reg-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Contraseña *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Repetir contraseña"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
                data-testid="merchant-confirm-password-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="callbackUrl">Webhook URL (Opcional)</Label>
            <Input
              id="callbackUrl"
              name="callbackUrl"
              type="url"
              placeholder="https://tu-sitio.com/webhook"
              value={formData.callbackUrl}
              onChange={handleChange}
              disabled={loading}
            />
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              URL donde recibirás notificaciones de pagos
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="returnUrl">Return URL (Opcional)</Label>
            <Input
              id="returnUrl"
              name="returnUrl"
              type="url"
              placeholder="https://tu-sitio.com/gracias"
              value={formData.returnUrl}
              onChange={handleChange}
              disabled={loading}
            />
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              URL donde redirigir al cliente después del pago
            </p>
          </div>

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={loading}
            data-testid="merchant-register-submit-btn"
          >
            {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            ¿Ya tienes cuenta?{' '}
            <Link to="/portal/login" className="text-[hsl(var(--brand-teal))] hover:underline font-medium">
              Inicia sesión
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}
